import type { ReactNode } from 'react';
import { CircleAlert, CircleX, RefreshCw } from 'lucide-react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import './results_notice.css';

/** O aviso de estado de uma lista de resultados. Quem o monta é o chamador, a
 *  partir do que a leitura da página diz.
 *
 *  `tone` decide ícone, urgência E FORMA. `warning` (a busca venceu) e `error`
 *  (falha nossa ou do fornecedor) pedem uma decisão do viajante, então viram
 *  cartão sobre a lista. `loading` não pede nada — vira uma linha discreta ao
 *  lado da contagem, com o girador e só o `title`; um cartão para trabalho em
 *  curso empurrava para baixo da dobra as linhas que a busca JÁ trouxe. */
export type ResultsNoticeView = {
  tone: 'loading' | 'warning' | 'error';
  title: string;
  /** A frase que explica. Opcional porque o tom `loading` não a tem: uma linha
   *  no cabeçalho não é lugar de parágrafo. */
  message?: string;
  actionLabel?: string;
  onAction?: () => void;
};

const NOTICE_ICON = {
  warning: CircleAlert,
  error: CircleX,
} as const;

export type ResultsDecisionNotice = Omit<ResultsNoticeView, 'tone'> & {
  tone: keyof typeof NOTICE_ICON;
};

/** O aviso que vira CARTÃO — carregando fica de fora e vai para o cabeçalho.
 *  Reconstruído em vez de repassado porque o `tone` do tipo público inclui
 *  `loading`, e é a checagem aqui que estreita o campo. */
export function decisionNotice(
  notice: ResultsNoticeView | undefined,
): ResultsDecisionNotice | undefined {
  return notice && notice.tone !== 'loading' ? { ...notice, tone: notice.tone } : undefined;
}

/**
 * O cartão de aviso sobre a lista. Só os dois tons que esperam uma decisão
 * chegam aqui — carregando é a linha discreta do cabeçalho. `role="alert"` no
 * erro e `status` no aviso: falha interrompe, busca vencida é cortesia. Um
 * botão sem rótulo não é montado.
 */
export function ResultsNotice({ notice }: { readonly notice: ResultsDecisionNotice }): ReactNode {
  const Icon = NOTICE_ICON[notice.tone];
  return (
    <div
      className="iuk-results-notice"
      data-tone={notice.tone}
      role={notice.tone === 'error' ? 'alert' : 'status'}
    >
      <Icon aria-hidden="true" />
      <strong>{notice.title}</strong>
      {notice.message && <p>{notice.message}</p>}
      {notice.onAction && notice.actionLabel && (
        <div className="iuk-results-notice-actions">
          {/* Sólido: é a única saída do estado, e no meio de uma lista de cartões
              um botão de contorno se perdia entre as linhas. */}
          <Button type="button" onClick={notice.onAction}>
            <RefreshCw aria-hidden="true" />
            {notice.actionLabel}
          </Button>
        </div>
      )}
    </div>
  );
}
